import React, { useState } from 'react';
import { JcColumn } from '../JcBox';
import { JcNote } from '../JcNote';
import { JcCommonProps, JcUtils } from '../../utils/JcUtils';
import { JcBuiltInIcon } from '../icon/JcBuiltInIcons';
import { JcButton, JcButtonType } from './JcButton';
import { JcPopupButton } from './JcPopupButton';

export interface JcActionMenuItem {
    readonly id: string;
    readonly label: string;
    /** A one-line semantics note under the label — what the action does that the label alone can't say (e.g. "the score so far is kept"). */
    readonly description?: string;
    readonly onSelect: () => void;
    /** Renders in the delete/error palette (JcButtonType.DELETE) for destructive items. */
    readonly destructive?: boolean;
    readonly disabled?: boolean;
}

export interface JcActionMenuProps extends JcCommonProps {
    readonly items: JcActionMenuItem[];
    /** Accessible name for the icon-only "⋮" trigger, e.g. "Game actions". */
    readonly ariaLabel: string;
}

/**
 * A "⋮" overflow menu for secondary actions that would otherwise clutter a page
 * with same-weight buttons — keep frequent actions visible and tuck the rest
 * behind this. Selecting an item closes the menu before running it, so a
 * confirmation dialog opened from `onSelect` never has to fight the popup for
 * focus/z-index. `anchored` flips the menu up/right instead of clipping off
 * the edge of the viewport, regardless of where the trigger sits. Group
 * unrelated actions (e.g. scoring corrections vs. game-status changes) into
 * separate `JcActionMenu`s rather than one combined list.
 */
export const JcActionMenu = (props: JcActionMenuProps): React.ReactElement => {
    const [open, setOpen] = useState(false);
    const commonProps = JcUtils.commonProps(props, ['jc-action-menu']);

    const select = (item: JcActionMenuItem) => {
        setOpen(false);
        item.onSelect();
    };

    return (
        <JcPopupButton
            {...commonProps}
            trigger={JcBuiltInIcon.ELLIPSIS_VERTICAL}
            ariaLabel={props.ariaLabel}
            open={open}
            setOpen={setOpen}
            anchored
            popupContent={
                <JcColumn className="jc-action-menu-list">
                    {props.items.map(item => (
                        <JcButton
                            key={item.id}
                            className="jc-action-menu-item"
                            buttonType={item.destructive ? JcButtonType.DELETE : JcButtonType.GHOST}
                            disabled={item.disabled}
                            onClick={() => select(item)}
                        >
                            {item.description === undefined ? item.label : (
                                <JcColumn className="jc-action-menu-item-content">
                                    {item.label}
                                    <JcNote text={item.description} />
                                </JcColumn>
                            )}
                        </JcButton>
                    ))}
                </JcColumn>
            }
        />
    );
};
